import { useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { animated, useSpring } from "@react-spring/web";

import { useAppState } from "../../providers/app-state"
import { useMeasure } from "react-use";
import Skeleton from 'react-loading-skeleton';

import { BsQuestionCircle, BsTelephoneFill } from "react-icons/bs"
import { IoIosArrowDown } from 'react-icons/io'
import { date_step_rental_car_check_text } from "../../lang/da";

const Timeslots = ({ goNext, loading, openSidebar }) => {
    const { state, dispatchMultiple, dispatch } = useAppState();

    const hasService = useMemo(() => {
        return state.selectedRepairs.some(rep => rep.group_id === 15 || rep.group_id === 31)
    }, [state.selectedRepairs])


    const pickupTimes = useMemo(() => {
        const stdHours = [
            "07:30",
            "10:00",
            "12:30",
            "14:00",
            "16:00"
        ]

        const timesAbove = stdHours.filter(hour => +hour > +state.deliveryTime)

        let firstDate = timesAbove.length >= 2 ? dayjs(state.selectedDate) : dayjs(state.selectedDate).add(1, 'day');

        firstDate = firstDate.day() === 6 ? firstDate.add(2, 'days') : firstDate;

        let secondDate = firstDate.day() === 5 ? firstDate.add(3, 'days') : firstDate.add(1, 'days');

        const firstDateHours = firstDate.isSame(state.selectedDate, 'date') ? timesAbove : stdHours;

        return [
            {
                date: firstDate,
                hours: [
                    ...firstDateHours.slice(1, -1),
                    firstDate.day() === 5 ? "15:30" : "16:00"
                ]
            },
            {
                date: secondDate,
                hours: [
                    ...stdHours.slice(0, -1),
                    secondDate.day() === 5 ? "15:30" : "16:00"
                ]
            }
        ]

    }, [state.deliveryTime, state.selectedDate])

    const hours = useMemo(() => {
        const today = dayjs();

        if (state.timeOption === 1) {
            return [
                "08:00",
                "12:30"
            ]
        }

        if (today.add(1, 'day').isSame(state.selectedDate, 'date')) {
            if (hasService) {
                return [
                    "12:30",
                ]
            }

            return [
                "12:30",
                "14:00",
                "15:00"
            ]
        }

        if (hasService) {
            return [
                "07:30",
                "09:00",
                "10:00",
                "11:00",
                "12:30",
            ]
        }

        return [
            "07:30",
            "09:00",
            "10:00",
            "11:00",
            "12:30",
            "14:00",
            "15:00"
        ]
    }, [state.selectedDate, hasService, state.timeOption])

    const [styles, api] = useSpring(() => ({
        from: {
            top: 0,
            bottom: 0,
            left: 2,
            right: 2,
            position: "absolute",
            translateY: state.timeOption === 4 ? "0%" : "100%"
        }
    }))

    useEffect(() => {
        (state.timeOption === 4 && api.start({ translateY: "0%" })) || api.start({ translateY: "100%" })
    }, [state.timeOption, api])

    const [ref, { height }] = useMeasure();

    useEffect(() => {
        if (state.timeOption !== 4) {
            api.start({ translateY: "100%" })
        }
    }, [state.selectedDate, state.timeOption, api])

    const withCarRef = useRef(null);

    useEffect(() => {
        if (state.timeOption === 1) {
            dispatch({
                key: "withCar",
                value: false
            })
        }
    }, [state.timeOption])

    const onSelectDelivery = (time) => {
        dispatch({
            key: "deliveryTime",
            value: time
        })
        if (state.timeOption === 1) {
            goNext();
        } else {
            api.start({ translateY: "0%" })
        }
    }

    const onSelectPickup = (time, date) => {
        dispatchMultiple([
            {
                key: "pickupTime",
                value: time
            },
            {
                key: "pickupDate",
                value: date
            },
        ])
        goNext();
    }

    if (loading) {
        return (
            <Skeleton
                className="h-64"
            />
        )
    }

    return (
        <div
            className="flex-1 basis-full relative overflow-y-hidden"
            style={{
                height: `${state.timeOption === 2 ? "auto" : height + 50 + "px"}`
            }}
        >
            <div
                className="bg-white rounded-md shadow-md divide-y-2 px-5"
            >
                {
                    state.timeOption !== 1 && (
                        <div
                            className="py-6 space-x-3 flex flex-wrap flex-row items-center"
                        >
                            <input
                                type="checkbox"
                                className="cursor-pointer accent-skgBlue"
                                id="car"
                                value={state.withCar}
                                onChange={() => {
                                    dispatch({
                                        key: "withCar",
                                        value: !state.withCar
                                    })
                                }}
                            />
                            <label className="cursor-pointer" htmlFor="car">{date_step_rental_car_check_text}</label>
                            <BsQuestionCircle
                                size={14}
                                className="hover:text-skgRed cursor-pointer"
                                onClick={() => {
                                    openSidebar(
                                        <div
                                            className="text-white p-5 space-y-4"
                                        >
                                            <h1
                                                className="text-2xl"
                                            >
                                                Skal du bruge en lånebil?
                                            </h1>
                                            <p>
                                                Vi kontakter dig, hvis ikke der er en lånebil ledig på den valgte dato.
                                            </p>
                                            <p>
                                                Der opkræves et forsikringsgebyr på 150 kr. pr. leje som forsikring for, at du ikke skal betale 5000 kr. i selvrisiko, hvis bilen bliver skadet i låneperioden.
                                            </p>
                                        </div>
                                    )
                                }}
                            />
                            <div
                                className="w-full"
                                style={{
                                    marginLeft: 25
                                }}
                            >
                                <ul>
                                    <li
                                        className="w-3/4 font-light text-sm"
                                    >
                                        <div
                                            className="flex flex-row justify-between"
                                        >
                                            <span>
                                                Lejeperiode
                                            </span>
                                            <span>
                                                0 kr.
                                            </span>
                                        </div>
                                    </li>
                                    <li
                                        className="w-3/4 font-light text-sm"
                                    >
                                        <div
                                            className="flex flex-row justify-between"
                                        >
                                            <span>
                                                Selvrisiko
                                            </span>
                                            <span>
                                                0 kr.
                                            </span>
                                        </div>
                                    </li>
                                    <li
                                        className="w-3/4 font-light text-sm"
                                    >
                                        <div
                                            className="flex flex-row justify-between"
                                        >
                                            <span>
                                                Obligatorisk forsikringsgebyr
                                            </span>
                                            <span>
                                                150 kr.
                                            </span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )
                }
                {
                    state.timeOption !== 2 && (
                        <div
                            className="space-y-4 py-6"
                        >
                            <div
                                className="flex justify-between flex-col"
                            >
                                <p
                                    className=""
                                >
                                    Vælg indleveringstidspunkt
                                </p>
                                {/* {
                                    hasService && (
                                        <p
                                            className="italic text-sm"
                                        >
                                            Fordi du har valgt et service vil vi gerne have bilen fra morgenstunden eller til middag.
                                        </p>
                                    )
                                } */}
                            </div>
                            <div
                                className="space-y-2"
                            >
                                <p>
                                    {state.selectedDate.locale("da").format("dddd DD. MMM 'YY")}
                                </p>
                                <div
                                    className="flex flex-row flex-wrap text-white"
                                >
                                    {
                                        hours.map((hour, i) => {
                                            return (
                                                <button
                                                    key={i}
                                                    className={`bg-skgBlue py-2 px-4 rounded-md mr-2 mb-2 transition-colors ease-in-out text-sm`}
                                                    onClick={() => {
                                                        onSelectDelivery(hour)
                                                    }}
                                                >
                                                    {hour}
                                                </button>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        </div>
                    )
                }
                <animated.div
                    style={styles}
                    className="rounded-md bg-skgBlue p-5 space-y-5 shadow-md"
                >
                    <div
                        ref={ref}
                        className="space-y-3"
                    >
                        <div
                            className="flex justify-between"
                        >
                            <p
                                className="font-bold text-white"
                            >
                                Vælg afhentningstidpunkt
                            </p>
                            {
                                state.timeOption !== 4 && (
                                    <IoIosArrowDown
                                        color="white"
                                        size={30}
                                        onClick={() => {
                                            api.start({ translateY: "100%" })
                                        }}
                                    />
                                )
                            }
                        </div>
                        {
                            pickupTimes.map((x, i) => {
                                return (
                                    <div
                                        className="space-y-3"
                                        key={i}
                                    >
                                        <p
                                            className="text-white"
                                        >
                                            {x.date.locale('da').format("dddd DD. MMM 'YY")}
                                        </p>
                                        {
                                            x.hours.map((hour, i) => {
                                                return (
                                                    <button
                                                        key={i}
                                                        className={`${hour === state.pickupTime && x.date.isSame(state.pickupDate, 'date') ? "bg-white" : "bg-white"} py-2 px-4 rounded-md mr-2 mb-2 transition-colors ease-in-out text-sm`}
                                                        onClick={() => {
                                                            onSelectPickup(hour, x.date)
                                                        }}
                                                    >
                                                        {hour}
                                                    </button>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            })
                        }
                        <div
                            className="pt-5"
                        >
                            <button
                                className="bg-skgRed text-white rounded-md py-2 px-6 flex items-center"
                                onClick={() => {
                                    dispatch({
                                        key: "getCall",
                                        value: true
                                    })
                                    goNext();
                                }}
                            >
                                <BsTelephoneFill
                                    className="mr-3"
                                />
                                Ring når den er færdig
                            </button>
                        </div>
                    </div>
                </animated.div>
            </div>
        </div>
    )
}

export default Timeslots;